import { EChartsOption, SeriesOption } from 'echarts';

import { EnergyDetailsSeries } from '../../model';
import { getStyleProp, telemetryStyles } from '../../styles.utils';
import { coreServicesLabels } from '../../../../core-services.service';
import { getPlaceholdersForEmptyDays, smartWrap } from '../../../utils/general.utils';

export const buildEnergyWeekGraph = (
  series: EnergyDetailsSeries[],
  dates: string[],
): ((
  weekIndex: number,
  serieName: string[],
  resume: { serieName: string; description: string; showTotalMaxValue?: boolean },
  withNegativeSide?: boolean,
) => EChartsOption) => {
  const xAxisData = [
    coreServicesLabels.mon,
    coreServicesLabels.tue,
    coreServicesLabels.wed,
    coreServicesLabels.thu,
    coreServicesLabels.fri,
    coreServicesLabels.sat,
    coreServicesLabels.sun,
  ];
  return (weekIndex, serieNames, resume, withNegativeSide = true) => ({
    title: {
      text: `${Math.abs(Number(series.find((prop) => prop.name === resume.serieName)?.values[weekIndex]
          .filter((value): value is number => value !== null)
          .reduce((prev, value) => prev + value, 0).toFixed()))} kWh - ${resume.description}`,
      left: 75,
      top: 20,
      textStyle: {
        color: getStyleProp('--grey-400'),
        fontSize: 16,
        fontFamily: getStyleProp('--font-family-brand'),
        fontWeight: 500,
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      confine: true,
      trigger: 'axis',
      formatter: (elements: any): string => {
        const { dataIndex } = elements[0];
        const date = dates[weekIndex * 7 + dataIndex].split('-').reverse().join('/');
        let template = `
        <div class="fx-row text-accent fx-align-center fx-justify-between mb-2">
          <div class="body-3 mr-3">${coreServicesLabels.day}</div>
          <div class="text-mono">${date}</div>
        </div>`;
        for (const element of elements) {
          const {
            seriesName,
            data: { value },
          } = element;
          const { name, color } = telemetryStyles(seriesName);
          if (value !== 0 && name !== 'emptyValue') {
            template += `
            <div class="fx-row ${color} fx-align-center fx-justify-between">
              <div class="body-3 mr-3">${name}</div>
              <div class="text-mono">${Math.abs(value).toFixed(2)} kWh</div>
            </div>`;
          }
        }
        return template;
      },
      backgroundColor: getStyleProp('--grey-100'),
      borderColor: getStyleProp('--grey-100'),
      borderRadius: 4,
      textStyle: {
        color: getStyleProp('--white'),
      },
    },
    xAxis: {
      data: xAxisData,
      axisLine: { onZero: true, show: false },
      splitLine: { show: false },
      splitArea: { show: false },
      axisTick: {
        show: false,
      },
    },
    grid: {
      show: false,
      top: '25%',
      bottom: '10%',
      left: '10%',
      right: '5%',
    },
    yAxis: {
      max: ({ max, min }) =>
        Math.abs(max) >= Math.abs(min) ? Math.abs(max) : Math.abs(min),
      min: ({ max, min }) =>
        withNegativeSide
          ? -(Math.abs(max) >= Math.abs(min) ? Math.abs(max) : Math.abs(min))
          : 0,
      type: 'value',
      name: '[kWh]',
      nameLocation: 'end',
      nameTextStyle: {
        color: getStyleProp('--grey-500'),
        fontWeight: 'bold',
      },
      splitLine: { show: false },
      axisLabel: {
        showMinLabel: false,
        formatter: (value: number) => Math.abs(value),
        showMaxLabel: false,
        color: getStyleProp('--grey-500'),
        fontWeight: 'bold',
      },
    },
    series: [
      ...series
        .filter((serie) => serieNames.includes(serie.name))
        .map((serie) => ({
          name: serie.name,
          type: 'bar',
          stack: 'one',
          labelLayout: (params) => ({
            x: params.rect.x + 10,
            y: params.rect.y + params.rect.height / 2,
            verticalAlign: 'middle',
            align: 'left',
          }),
          color: serie.color,
          data: serie.values[weekIndex].map((val) => ({value: val})),
        })) as SeriesOption[],
        //placeholder series
        ...getPlaceholdersForEmptyDays(series
        .filter((serie) => serieNames.includes(serie.name))
        .map((serie) => {
          return serie.values[weekIndex].map((val) => {
            if (val === null) {
              const negativeValues = serie.values[weekIndex]
                .filter((v): v is number => v !== null && v !== 0)
                .every((v) => v < 0);

                const placeholderValue = () => {
                  const values = serie.values[weekIndex];

                  const finiteValues = values.filter(v => v !== null && !Number.isNaN(v));

                  if(finiteValues.length > 0) {
                    return Math.max(...finiteValues.map(x => Math.abs(x || 0)));
                  } else {
                    return 0;
                  }
                }
              return {
                value: negativeValues ? -1 * placeholderValue() : placeholderValue(),
                itemStyle: {color: '#eaeaea'}
              };
            } else {
              return {value: null};
            }
          });
        }).map((mappedSeries) => ({
          name: 'emptyValue',
          type: 'bar',
          stack: 'one',
          data: mappedSeries
        } as SeriesOption )))
      ]
  });
};

export const buildEnergyPieChart =
  (
    series: EnergyDetailsSeries[],
  ): ((
    resume: { seriesName: string[] ,graphType: 'selfConsume' | 'selfProduction'}
  ) => EChartsOption) =>
  (resume) => ({
    title: {
      text: resume.graphType === 'selfConsume' ?
      [
        `{name|${telemetryStyles('selfConsume').name.split(' ').join('\n')}}`,
        `{percentage|${ ((series.find((serie) => serie.name === 'selfConsume')?.selfConsumePercentage || 0) * 100 ).toFixed(2) +'%'}}`
      ].join('\n') :
      [
        `{name|${telemetryStyles('selfProduction').name.split(' ').join('\n')}}`,
        `{percentage|${ ((series.find((serie) => serie.name === 'selfProduction')?.selfConsumePercentage || 0) * 100 ).toFixed(2) +'%'}}`
      ].join('\n'),
      left: 'center',
      top: 'center',
      textStyle: {
        rich: {
          name: {
            fontSize: 14,
            fontStyle: 'italic'
          },
          percentage: {
            color:  getStyleProp('--red-500'),
            fontSize: 14,
            fontWeight: 'bold',
            lineHeight: 18
          }
        }
      }
   },
    series: [
      {
        type: 'pie',
        startAngle: 90,
        endAngle: -270,
        tooltip: {
          trigger: 'item'
        },
        progress: {
          show: true,
          overlap: false,
          roundCap: true,
          clip: false,
          itemStyle: {
            borderWidth: 1,
            borderColor: getStyleProp('--grey-400'),
          },
        },
        axisLine: {
          lineStyle: {
            width: 40,
          },
        },
        splitLine: {
          show: false,
          distance: 0,
          length: 10,
        },
        axisTick: {
          show: false,
        },
        axisPointer: {
          type: 'none'
       },
        axisLabel: {
          show: false,
          distance: 50,
        },
        radius: ['35%', '55%'],
        avoidLabelOverlap: true,
        itemStyle: {
        borderColor: getStyleProp('--white'),
        borderWidth: 2
        },
        grid: {
          show: true,
          top: '0',
          bottom: '40',
          left: '40',
          right: '40',
        },
        label: {
          show: true,
          fontSize: 12,
          formatter: (d) => {
            return `{a|${smartWrap(d.name, 8)}}\n{b|${d.value}%}`;
          },
          rich: {
            a: {
              align: 'center'
            },
            b: {
              fontWeight: 'bold',
              fontSize: 14,
            }
          }
        },
        data: series.filter((s) => resume.seriesName.includes(s.name)).map((serie) => {
          const { name, scssColor, offsetCenter, detail } = telemetryStyles(serie.name);
          return {
            name: name,
            value: Number(((serie?.selfConsumePercentage || 0) * 100).toFixed(2)),
            itemStyle: {
              color: getStyleProp(scssColor as string),
            },
            title: {
              offsetCenter,
              fontSize: 13,
            },
          };
        }),
      },
    ],
  });

export const buildEnergySelfConsume =
  (
    series: EnergyDetailsSeries[],
  ): ((
    resume: { seriesName: string[] }
  ) => EChartsOption) =>
  (resume) => ({
    series: [
      {
        type: 'gauge',
        startAngle: 90,
        endAngle: -270,
        pointer: {
          show: false,
        },
        progress: {
          show: true,
          overlap: false,
          roundCap: true,
          clip: false,
          itemStyle: {
            borderWidth: 1,
            borderColor: '#464646',
          },
        },
        axisLine: {
          lineStyle: {
            width: 40,
          },
        },
        splitLine: {
          show: false,
          distance: 0,
          length: 10,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
          distance: 50,
        },
        title: {
          fontSize: 12,
        },
        detail: {
          width: 50,
          height: 14,
          fontSize: 14,
          color: 'auto',
          borderColor: 'auto',
          borderRadius: 20,
          borderWidth: 1,
          formatter: '{value}%',
        },
        data: series.filter((s) => resume.seriesName.includes(s.name)).map((serie) => {
          const { name, scssColor, offsetCenter, detail } = telemetryStyles(serie.name);
          return {
            name,
            value: Number(((serie?.selfConsumePercentage || 0) * 100).toFixed(2)),
            itemStyle: {
              color: getStyleProp(scssColor as string),
            },
            color: 'blue',
            title: {
              offsetCenter,
              fontSize: 13,
            },
            detail,
          };
        }),
      },
    ],
  });

export const buildPortalEnergyFullMonthGraph = (
  series: EnergyDetailsSeries[],
  dates: string[],
): EChartsOption => {
  const selected = ['EG1', 'EG2', 'EG3', 'EG4', 'EG5', 'EG6'];
  // month that is repeated more times in the dates array
  const month = dates.map((date) => date.split('-')[1]).reduce((a, b, i, arr) => {
    return arr.filter((v) => v === a).length >= arr.filter((v) => v === b).length ? a : b;
  }, '');

  // dates that are in the month
  const data = series.map((serie) => ({
    ...serie,
    values: (serie.values || []).flat().filter((_, i) => dates[i].split('-')[1] === month),
  })).filter((serie) => selected.includes(serie.name));

  dates = dates.filter((date) => date.split('-')[1] === month);

  return ({
    legend: {
      show: false,
      selected: {
        EG1: true,
        EG2: true,
        EG3: true,
        EG4: true,
        EG5: true,
        EG6: true,
      },
    },
    tooltip: {
      confine: true,
      trigger: 'axis',
      formatter: (elements: any): string => {
        const { dataIndex } = elements[0];
        const date = dates[dataIndex];
        let template = `
        <div class="fx-row text-accent fx-align-center fx-justify-between mb-2">
          <div class="body-3 mr-3">${coreServicesLabels.day}</div>
          <div class="text-mono">${date}</div>
        </div>`;
        for (const element of elements) {
          const {
            seriesName,
            data: value,
          } = element;
          const { name, color } = telemetryStyles(seriesName);
          if (value !== 0) {
            template += `
            <div class="fx-row ${color} fx-align-center fx-justify-between">
              <div class="body-3 mr-3">${name}</div>
              <div class="text-mono">${Math.abs(value).toFixed(2)} kWh</div>
            </div>`;
          }
        }
        return template;
      },
      backgroundColor: getStyleProp('--grey-100'),
      borderColor: getStyleProp('--grey-100'),
      borderRadius: 4,
      textStyle: {
        color: getStyleProp('--white'),
      },
    },
    yAxis: {
      type: 'value',
      axisLine: {
        show: false,
        onZero: false,
      },
      splitLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      name: 'kWh',
      nameLocation: 'middle',
      nameTextStyle: {
        color: getStyleProp('--grey-400'),
        fontWeight: 'bold',
      },
      axisLabel: {
        show: false,
      },
    },
    xAxis: {
      data: dates.map((_, index) => index + 1),
      type: 'category',
      axisLine: { onZero: true },
      splitLine: { show: false },
      splitArea: { show: false },
      axisLabel: {
        color: getStyleProp('--grey-200'),
        fontWeight: 'bold',
        showMinLabel: false,
        interval: 0,
        showMaxLabel: false,
        hideOverlap: true,
        rotate: 45,
      },
      axisTick: {
        alignWithLabel: false,
      },
    },
    grid: {
      show: false,
      top: '0',
      bottom: '40',
      left: '40',
      right: '40',
    },
    series: data.map((serie) => ({
      name: serie.name,
      type: 'bar',
      color: serie.color,
      data: (serie.values?.flat() || []).filter((value): value is number => value !== null),
      barGap: 0,
      stack: ((name) => {
        switch (name) {
          case 'EG1':
            return 'one';
          case 'EG2':
            return 'two';
          case 'EG3':
            return 'two';
          case 'EG4':
            return 'three';
          case 'EG5':
            return 'four';
          case 'EG6':
            return 'four';
          default:
            return 'x';
        }
      })(serie.name),

      emphasis: {
        focus: 'series',
        itemStyle: {
          shadowBlur: 10,
          shadowColor: 'rgba(0,0,0,0.3)'
        }
      }
    })),
  })
};
